import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

// RxJS
import { catchError, filter, of, Subject, switchMap, takeUntil } from 'rxjs';

// Services
import { AlertService } from '@modules/elements/services/alert.service';
import { NotificationService } from '@modules/elements/services/notification.service';
import { ProjectService } from '@modules/project/services/project.service';
import { UsersService } from '@modules/users/services/users.service';

// Types
import { Option } from '@modules/elements/types/option';
import { Project } from '@modules/project/types/project';
import { AwsOverrides, ServerTemplate, ServerTemplateType } from '@modules/project/types/server-template';
import { TaskTemplate } from '@modules/project/types/task-template';
import { UserPermissions } from '@modules/users/types/user';
import { RecordOptions } from '@modules/records/types/record-options';

@Component({
    selector: 'app-project-server',
    templateUrl: './project-server.component.html',
    styleUrl: './project-server.component.less',
    standalone: false
})
export class ProjectServerComponent implements OnInit, OnChanges, OnDestroy {

  // Inputs
  @Input() server?: ServerTemplate;
  @Input() project?: Project;

  // Public
  public tasks: string = '';
  public permissions?: UserPermissions[];
  public showGraph = false;
  public recordOptions = new RecordOptions();
  public destinationOptions: Option<string>[] = [];
  public settingsText: string = '';
  public awsOverridesText: string = '';

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private projectService: ProjectService,
    private usersService: UsersService,
    private notificationService: NotificationService,
    private alertService: AlertService
  ) {
  }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
  }

  ngOnChanges(changes: SimpleChanges): void {
      if ('server' in changes) {
        this.settingsText = JSON.stringify(this.server?.settings, null, 2);
        this.awsOverridesText = this.server?.awsOverrides ? JSON.stringify(this.server.awsOverrides, null, 2) : '';
        this.tasks = JSON.stringify(this.server?.taskTemplates, null, 2);
        if (this.server && !this.server.deliverables) {
          this.server.deliverables = [];
        }
      }
      if ('project' in changes) {
        const destinations = Object.entries(this.project?.destinations || {}).map(([id, data]) => ({id, ...data})); 
        this.destinationOptions = destinations.map(item => new Option(item.id, item.name || item.id));
      }
    }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  addDeliverableFormat(): void {
    this.server?.deliverables?.push({format: 'j2ki', destinations: []});
    this.updateServer();
  }

  deleteDeliverable(index: number): void {
    this.server?.deliverables?.splice(index, 1);
    this.updateServer();
  }

  updateDeliverableFormat(option: any, index: number): void {
    if (option && this.server?.deliverables) {
      this.server.deliverables[index].format = option.value;
      this.updateServer();
    }
  }

  updateDeliverableDestination(options: any, index: number): void {
    if (options && this.server?.deliverables) {
      this.server.deliverables[index].destinations = (options as Option<any>[]).map(option => option?.value || option);
      this.updateServer();
    }
  }

  /**
   * Actions
   */

  setGeneralTemplate(): void {
    this.tasks = JSON.stringify(TaskTemplate.getGeneralTasks(), null, 2);
  }

  setTranscodeTemplate(): void {
    this.tasks = JSON.stringify(TaskTemplate.getTranscodeTasks(), null, 2);
  }

  setNflTemplate(): void {
    this.tasks = JSON.stringify(TaskTemplate.getNflTasks(), null, 2);
  }

  setC3Template(): void {
    this.tasks = JSON.stringify(TaskTemplate.getC3Tasks(), null, 2);
  }

  changeServerType(type: ServerTemplateType): void {
    if (this.server) {
      this.server.type = type;
      this.updateServer();
    }
  }

  updateServer(): void {
    if (this.project?.id && this.server?.id) {
      if (!this.project.name?.length) {
        this.project.name = 'Server';
      }
      if (!this.server.serverKey?.length) {
        this.server.serverKey = '';
      }
      this.projectService.updateServerTemplates(this.project?.id, this.server);
    }
  }

  updateSettings(): void {
    let settings;
    try {
      settings = JSON.parse(this.settingsText); 
    } catch (error: any) {
      this.notificationService.error('Update Server Error:', (error as SyntaxError).message, true);
    }
    if (this.server && settings) {
      this.server.settings = settings;
      this.updateServer();
    }
  }

  updateAwsOverrides(): void {
    if (!this.server) { return; }
    // An empty textarea means "no overrides". The server merges the template, so an omitted key
    // keeps the stored value — only an explicit null clears it.
    if (!this.awsOverridesText.trim().length) {
      this.server.awsOverrides = null;
      this.updateServer();
      return;
    }
    let awsOverrides: AwsOverrides | undefined;
    try {
      awsOverrides = JSON.parse(this.awsOverridesText);
    } catch (error: any) {
      this.notificationService.error('Update Server Error:', (error as SyntaxError).message, true);
      return;
    }
    if (typeof awsOverrides !== 'object' || awsOverrides === null || Array.isArray(awsOverrides)) {
      this.notificationService.error('Update Server Error:', 'AWS Overrides must be a JSON object', true);
      return;
    }
    this.server.awsOverrides = awsOverrides;
    this.updateServer();
  }

  saveTasks(): void {
    let taskTemplates: TaskTemplate[] | undefined;
    try {
      taskTemplates = JSON.parse(this.tasks);
    } catch (error: any) {
      this.notificationService.error('Task update error:', (error as SyntaxError).message, true);
    }
    if (this.server && this.server.id && taskTemplates) {
      this.server.taskTemplates = taskTemplates;
      this.projectService.updateTaskTemplates(this.server.id, taskTemplates)
        .pipe(
          catchError(error => {
            if (error) {
              const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
              this.notificationService.error('Task update error: ', message);
            }
            return of();
          }),
          takeUntil(this.alive)
        )
        .subscribe(() => this.notificationService.success(`Tasks updated successfully`, '', true))
    }

  }

  deleteServer(): void {
    if (this.project?.id && this.server?.id)
    this.alertService.show(
      'Delete Server',
      `Would you like to delete "${this.server?.serverName}" record?`,
      ['Yes', 'No'],
      0
    )
      .pipe(
        filter(respose => respose === 0),
        switchMap(() => this.projectService.deleteServerTemplates(this.project!.id!, [this.server!.id!]))
      )
      .subscribe(response => {
        this.notificationService.success(`Server "${this.server?.serverName}" deleted successfully`, '', true);
      });
  }

}
